/**
 * MAUD ERP Routes Configuration (Cleaned)
 *
 * Routes for: Assets, Collections, Finance, Leases & Agreements, Display Devices, LCMS, Portal Management
 */

import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import PrivateRoute from '../../../../components/PrivateRoute';

// Layout
const MaudErpLayoutWrapper = lazy(() => import('../MaudErpLayoutWrapper'));

// Home Page
const MaudErpHomePage = lazy(() => import('../HomePage'));

// Simple pass-through layout for nested routes
const PassThroughLayout = () => <Outlet />;

// Import routes from each module's config
import { assetManagementRoutes } from '../asset-management/routes/config';
import { leasesAgreementsRoutes } from '../leases-and-agreements/routes/config';
// Retired with the finance-management route block below — see ../index.tsx.
// import { financeManagementRoutes } from '../finance-management/routes/config';
import { collectionsRoutes } from '../collections/routes/config';
import { displayDevicesRoutes } from '../display-devices/routes/config';
import { legalCaseManagementRoutes } from '../legal-case-management/routes/config';
import { schemeBookkeepingRoutes } from '../scheme-bookkeeping/routes/config';
import { integrationSecretsRoutes } from '../integration-secrets/routes/config';
import { smsHistoryRoutes } from '../sms-history/routes/config';
import { municipalCouncilRoutes } from '../municipal-council/routes/config';

// Municipal Council Layout
const MunicipalCouncilLayout = lazy(() => import('../municipal-council/MunicipalCouncilLayout'));

// Portal Management
const PortalHome = lazy(() => import('../../../../pages/apps/portal/index'));
const PortalSite = lazy(() => import('../../../../pages/apps/portal/site/index'));


// Child routes for MAUD ERP
export const maudErpChildRoutes: RouteObject[] = [
  {
    index: true,
    element: <MaudErpHomePage />
  },
  // Asset Management routes
  {
    path: 'asset-management',
    element: <PassThroughLayout />,
    children: assetManagementRoutes
  },
  // Leases and Agreements routes
  {
    path: 'leases-and-agreements',
    element: <PassThroughLayout />,
    children: leasesAgreementsRoutes
  },
  // Finance Management routes — RETIRED, see the note in ../index.tsx.
  // Finance2 is the Finance module now; this module's master screens are still
  // imported directly by finance2/routes/config.tsx, so the code stays.
  // {
  //   path: 'finance-management',
  //   element: <PassThroughLayout />,
  //   children: financeManagementRoutes
  // },
  // Collections routes
  {
    path: 'collections',
    element: <PassThroughLayout />,
    children: collectionsRoutes
  },
  // Display Devices routes
  {
    path: 'display-devices',
    element: <PassThroughLayout />,
    children: displayDevicesRoutes
  },
  // Legal Case Management routes
  {
    path: 'legal-case-management',
    element: <PassThroughLayout />,
    children: legalCaseManagementRoutes
  },
  // LRS / BPS applications — data pushed in by the scheme portals
  {
    path: 'scheme-applications',
    element: <PassThroughLayout />,
    children: schemeBookkeepingRoutes
  },
  // Third-party integration secrets — granted per employee through the menu
  {
    path: 'integration-secrets',
    element: <PassThroughLayout />,
    children: integrationSecretsRoutes
  },
  // SMS History — send + OTP verification status, granted through the menu
  {
    path: 'sms-history',
    element: <PassThroughLayout />,
    children: smsHistoryRoutes
  },
  // Municipal Council routes
  {
    path: 'municipal-council',
    element: <MunicipalCouncilLayout />,
    children: municipalCouncilRoutes
  },
  // Portal Management routes
  {
    path: 'portal-management',
    element: <PassThroughLayout />,
    children: [
      {
        index: true,
        element: <PortalHome />
      },
      {
        path: ':siteId',
        element: <PortalSite />
      }
    ]
  }
];

// Main MAUD ERP route configuration
export const maudErpRoutes: RouteObject = {
  path: '/app/maud-erp',
  element: <PrivateRoute><MaudErpLayoutWrapper /></PrivateRoute>,
  children: maudErpChildRoutes
};

export default maudErpRoutes;
